import React, { useCallback, useState } from "react";
import { toast } from "sonner";

import { useAgents } from "@/shared/api/agents";
import { ApiError } from "@/shared/api/errors";
import { useAgentUsers, useOnboardingFlow, type PeopleFilter } from "@/shared/api/onboarding";
import { Pane, RowList } from "@/shared/components/settings-atoms";
import { ViaIcon } from "@/shared/components/ViaIcon";
import {
  TOO_MANY_RULES, useCreateRule, useDeleteRule, useOrderRules, usePatchRule, useRuleCheck, useRules,
} from "../api/policyRules";
import { MAX_RULES } from "../types";
import { AddPolicy } from "./policies/AddPolicy";
import { CheckPerson } from "./policies/CheckPerson";
import { EveryoneElseEditor } from "./policies/EveryoneElseEditor";
import { PolicyCard } from "./policies/PolicyCard";
import { PolicyEditor } from "./policies/PolicyEditor";
import { DEFAULT_REFUSAL, chipsOf, effectOf, fallbackOf, ownLimitsOf } from "./policies/copy";
import type { Preset } from "./policies/presets";

import type { Agent, AgentRow, InterfaceType } from "@/shared/types";
import type { Rule, RuleCheckBody, RulePatch, RuleWhen } from "../types";

/** Where someone reaches the agent from: the client app and the chat channels. */
const ENTRY: InterfaceType[] = ["web", "whatsapp", "telegram", "slack"];
/** The tester offers the first people the People list shows. */
const EVERYONE_FILTER: PeopleFilter = { status: null, q: "" };
const PICKS = 5;
/** The editor's selection for the Everyone else card. */
const EVERYONE = "everyone";

/* Policies — what happens to the people who reach the agent (who can find it is General's
   Visibility): each policy matches people by channel, email or onboarding answer, then lets
   them in with limits or keeps them out. Applies at once, never a draft. */
export default function PoliciesSection({ agent }: { agent: Agent }) {
  const agents = useAgents();
  const row = agents.data?.find((found) => found.id === agent.id);
  return (
    <Pane
      title="Policies"
      desc="What happens to the people who reach this agent. Each policy matches people by channel, email or onboarding answer, then lets them in with limits or keeps them out. Changes apply at once."
    >
      {row && <Policies agent={row} />}
    </Pane>
  );
}

/* The canvas of policies in the order they're tried — someone reaches the agent, meets each
   in turn, and anyone left over gets Everyone else — beside the open one's editor, and the
   tester under both. */
function Policies({ agent }: { agent: AgentRow }) {
  const list = useRules(agent.id);
  const flow = useOnboardingFlow(agent.agent_onboarding_flow_id);
  const folks = useAgentUsers(agent.id, EVERYONE_FILTER);
  const create = useCreateRule();
  const patch = usePatchRule(agent.id);
  const remove = useDeleteRule();
  const order = useOrderRules(agent.id);

  const [selected, setSelected] = useState<string | null>(null);
  /** A preset's policy made off, and the condition the editor starts it on (Blank: none): it
      goes on once its first condition holds someone. */
  const [starting, setStarting] = useState<{ id: string; key?: keyof RuleWhen } | null>(null);
  const [probe, setProbe] = useState<RuleCheckBody | null>(null);
  const check = useRuleCheck(probe);
  const [drag, setDrag] = useState<{ armed: string | null; dragging: string | null; ids: string[] | null }>({ armed: null, dragging: null, ids: null });
  const onProbe = useCallback((body: RuleCheckBody | null) => setProbe(body), []);

  if (!list.data) {
    return <p className="m-0 text-[12.5px] text-[var(--text-tertiary)]">{list.isError ? "The policies couldn't be loaded." : "Loading…"}</p>;
  }

  const saved = list.data.rules;
  const byId = new Map(saved.map((p) => [p.id, p]));
  const policies = drag.ids ? drag.ids.map((id) => byId.get(id)).filter((p): p is Rule => !!p) : saved;
  const fallback = fallbackOf(agent);
  const questions = (flow.data?.blocks ?? [])
    .flatMap((b) => (b.type === "questions" ? b.fields : []))
    .filter((q): q is typeof q & { key: string } => !!q.key)
    .map((q) => ({ key: q.key, label: q.label }));
  const questionLabel = (key: string) => questions.find((q) => q.key === key)?.label;
  const open = selected === EVERYONE ? null : policies.find((p) => p.id === selected) ?? policies[0] ?? null;
  const hitId = check.data ? check.data.rule_id : undefined;

  const add = (at: number, preset: Preset) => {
    if (saved.length >= MAX_RULES) {
      toast.error(`${agent.name} has ${MAX_RULES} policies, the most it can have`);
      return;
    }
    create.mutate({ agent_id: agent.id, position: at, ...preset.body }, {
      onSuccess: (made) => {
        setSelected(made.id);
        setStarting(made.enabled ? null : { id: made.id, key: preset.starts });
      },
      onError: (err) => {
        const api = err instanceof ApiError ? err : null;
        toast.error(
          api?.code === TOO_MANY_RULES ? `${agent.name} has ${MAX_RULES} policies, the most it can have` : err.message,
          { description: api?.request_id ? `ref ${api.request_id}` : undefined },
        );
      },
    });
  };

  const change = (p: Rule, body: RulePatch) => {
    // a preset's policy, made off, goes on once its first condition holds someone
    const mine = starting?.id === p.id;
    const goesOn = mine && !p.enabled && body.when && Object.keys(body.when).length > 0 && body.enabled === undefined;
    // switched by hand, it's the owner's to switch from then on
    if (goesOn || (mine && body.enabled !== undefined)) setStarting(null);
    patch.mutate({ id: p.id, body: goesOn ? { ...body, enabled: true } : body }, {
      onSuccess: () => {
        if (goesOn) toast.success(`${p.name} is on`);
      },
    });
  };

  const drop = (p: Rule) =>
    remove.mutate(p.id, {
      onSuccess: () => {
        toast.success("Policy deleted");
        setSelected((now) => (now === p.id ? null : now));
      },
    });

  const reorder = (ids: string[]) => {
    if (ids.join() !== saved.map((p) => p.id).join()) order.mutate(ids);
  };
  const move = (i: number, by: -1 | 1) => {
    const ids = policies.map((p) => p.id);
    [ids[i], ids[i + by]] = [ids[i + by], ids[i]];
    reorder(ids);
  };

  const shadowAbove = (p: Rule) => {
    const above = policies.slice(0, policies.indexOf(p)).filter((q) => q.enabled);
    return above.length === 1 ? above[0].name : null;
  };

  return (
    <section className="flex flex-col">
      <div className="mb-2 ml-1">
        <div className="text-[12.5px] font-medium text-[var(--text-secondary)]">
          Policies <span className="font-mono tabular-nums text-[11.5px] text-[var(--text-quaternary)]">{saved.length}</span>
        </div>
        <p className="m-0 mt-0.5 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">
          Checked top to bottom. The first policy that matches someone decides what happens; anyone left over gets Everyone else.
        </p>
      </div>
      <div className="flex flex-wrap items-start gap-5">
        <div
          aria-label="Policies in order"
          className="flex-[0_1_380px] min-w-[300px] flex flex-col rounded-[18px] ring-1 ring-[var(--hairline)] bg-[var(--surface-sunk)] wf-dots px-4 pt-5 pb-6"
        >
          <div className="self-center flex items-center gap-1.5 h-[30px] pl-1.5 pr-3 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] text-[12.5px] text-[var(--text-secondary)] whitespace-nowrap">
            <span className="flex gap-0.5">
              {ENTRY.map((type) => <ViaIcon key={type} type={type} />)}
            </span>
            Someone reaches {agent.name}
          </div>
          {policies.map((p, i) => (
            <React.Fragment key={p.id}>
              <AddPolicy disabled={create.isPending} onAdd={(preset) => add(i, preset)} />
              <PolicyCard
                n={i + 1}
                title={p.name || "Untitled policy"}
                action={p.action}
                off={!p.enabled}
                hit={hitId === p.id}
                count={p.matches.count}
                chips={chipsOf(p.when, questionLabel)}
                says={p.action === "block" ? `Told: ${p.message || DEFAULT_REFUSAL}` : ownLimitsOf(p)}
                selected={open?.id === p.id}
                onSelect={() => setSelected(p.id)}
                order={{
                  first: i === 0,
                  last: i === policies.length - 1,
                  onMove: (by) => move(i, by),
                  onDelete: () => drop(p),
                  drag: {
                    armed: drag.armed === p.id,
                    dragging: drag.dragging === p.id,
                    onArm: () => setDrag((d) => ({ ...d, armed: p.id })),
                    onStart: () => setDrag((d) => ({ ...d, dragging: p.id, ids: saved.map((q) => q.id) })),
                    onOver: () => setDrag((d) => {
                      if (!d.dragging || !d.ids || d.dragging === p.id) return d;
                      const ids = d.ids.filter((id) => id !== d.dragging);
                      ids.splice(d.ids.indexOf(p.id), 0, d.dragging);
                      return { ...d, ids };
                    }),
                    onEnd: () => {
                      if (drag.ids) reorder(drag.ids);
                      setDrag({ armed: null, dragging: null, ids: null });
                    },
                  },
                }}
              />
            </React.Fragment>
          ))}
          <AddPolicy disabled={create.isPending} onAdd={(preset) => add(policies.length, preset)} />
          <PolicyCard
            n={null}
            title="Everyone else"
            action="allow"
            hit={hitId === null}
            count={list.data.everyone_else.matches.count}
            chips={[]}
            says={`Anyone not matched above · ${effectOf(null, fallback)}`}
            selected={!open}
            onSelect={() => setSelected(EVERYONE)}
          />
          {policies.length === 0 && (
            <p className="m-0 mt-4 text-[12px] leading-[1.5] text-center text-[var(--text-tertiary)]">
              No policies yet: everyone {agent.name} lets in gets Everyone else. Press + to treat a domain, a channel or an onboarding answer differently.
            </p>
          )}
        </div>
        <RowList className="flex-[1_1_360px]">
          <div className="py-4">
            {open ? (
              <PolicyEditor
                key={open.id}
                policy={open}
                n={policies.indexOf(open) + 1}
                of={policies.length}
                agentName={agent.name}
                fallback={fallback}
                questions={questions}
                shadowAbove={shadowAbove(open)}
                starts={starting?.id === open.id ? starting.key : undefined}
                onPatch={(body) => change(open, body)}
                onDelete={() => drop(open)}
              />
            ) : (
              <EveryoneElseEditor agent={agent} matches={list.data.everyone_else.matches} />
            )}
          </div>
        </RowList>
      </div>
      <CheckPerson
        agentId={agent.id}
        people={(folks.data?.pages[0] ?? []).slice(0, PICKS)}
        policies={policies}
        result={check.data}
        fallback={fallback}
        onProbe={onProbe}
        onOpen={(id) => setSelected(id ?? EVERYONE)}
      />
    </section>
  );
}
